/**
 * Bouton flottant « Retour en haut » qui apparaît dès que l'utilisateur
 * a scrollé d'une distance configurable (défaut 400px).
 *
 * Usage :
 *   <ScrollToTop />            // valeurs par défaut (400px, bottom-right)
 *   <ScrollToTop threshold={800} />
 *   <ScrollToTop position="bottom-left" />
 */
import React, { useEffect, useState, useCallback } from 'react';
import { ChevronUp } from 'lucide-react';

interface Props {
  threshold?: number;             // px de scroll avant d'apparaître
  position?: 'bottom-right' | 'bottom-left' | 'top-right';
  containerRef?: React.RefObject<HTMLElement>; // si scroll dans un container, pas window
  className?: string;
  label?: string;
}

const ScrollToTop: React.FC<Props> = ({
  threshold = 400,
  position = 'bottom-right',
  containerRef,
  className = '',
  label = 'Retour en haut',
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const target: any = containerRef?.current ?? window;
    const handleScroll = () => {
      const scrollY = containerRef?.current
        ? containerRef.current.scrollTop
        : (window.pageYOffset || document.documentElement.scrollTop);
      setVisible(scrollY > threshold);
    };
    target.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => target.removeEventListener('scroll', handleScroll);
  }, [threshold, containerRef]);

  const scrollToTop = useCallback(() => {
    if (containerRef?.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [containerRef]);

  if (!visible) return null;

  const posClasses = {
    'bottom-right': 'bottom-6 right-6',
    'bottom-left': 'bottom-6 left-6',
    'top-right': 'top-24 right-6',
  }[position];

  return (
    <button
      onClick={scrollToTop}
      title={label}
      aria-label={label}
      className={`fixed ${posClasses} z-40 p-3 rounded-full shadow-lg
        bg-blue-600 hover:bg-blue-700 text-white
        transition-all duration-200 ease-out
        animate-in fade-in zoom-in
        ${className}`}
      style={{
        animation: 'scrollTopFadeIn 0.3s ease-out',
      }}
    >
      <ChevronUp size={20} />
      <style>{`
        @keyframes scrollTopFadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </button>
  );
};

export default ScrollToTop;
